/**
 * DeadlineCalendarExportModal
 *
 * PR-3 of the Watchtower Calendar Export feature.
 * See docs/architecture/watchtower-calendar-export.md §4 (modal),
 * §5 (callable contract), §6 (hook integration).
 *
 * Owns:
 *  - Form state (title, description, includeRecipientName, acknowledged)
 *  - Per-field dirty flags so toggle changes don't clobber user edits
 *  - The strict-sequential submit pipeline:
 *       audit (logDeadlineCalendarExport) → generate (buildDeadlineIcs) →
 *       trigger Blob download.
 *
 * Audit success is a HARD GATE — `.ics` is not built and no download fires
 * unless the audit callable returns `success: true`. Every failure mode
 * leaves the modal open with an inline banner and re-enabled inputs; the
 * user can retry.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import { FunctionsError } from 'firebase/functions';
import { AlertTriangle, CheckCircle2, Loader2, X } from 'lucide-react';
import { functions } from '../../lib/firebase';
import {
  DEADLINE_PRIORITIES,
  DEADLINE_TYPES,
  type Deadline,
} from './types';
import { buildDeadlineIcs } from '../../lib/calendar/icsGenerator';
import type { CalendarExportOptions } from '../../lib/calendar/types';

// ────────────────────────────────────────────────────────────────────────────
// Public types
// ────────────────────────────────────────────────────────────────────────────

/**
 * Reference to a deadline that the modal will fetch itself (via the
 * `getDeadline` callable) when opened. Use this from contexts that only
 * have a `DeadlineSummary` (e.g. card hover actions) — the modal owns the
 * loading + error UI so all failure paths share the inline error banner.
 */
export interface DeadlineRef {
  caseFileId: string;
  deadlineId: string;
}

export interface DeadlineCalendarExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Either a fully-loaded `Deadline` (preferred from contexts that already
   * have one — e.g. `DeadlineDetail`) OR a `DeadlineRef` to be fetched on
   * open. Exactly one should be provided per open; if both are set,
   * `deadline` wins.
   */
  deadline?: Deadline;
  deadlineRef?: DeadlineRef;
  /**
   * CaseFile.name — care recipient full name (v2.4 schema). May be
   * undefined while the parent's CaseFile context is still resolving;
   * when undefined or empty the PHI toggle is force-disabled and the audit
   * payload's `includedCategories.recipientName` is forced to `false` —
   * this prevents an audit entry from claiming PHI was included when the
   * generator could not actually inject it. (TASK-016 Codex remediation.)
   */
  recipientName?: string;
  onExported: (result: { auditLogged: boolean }) => void;
}

interface GetDeadlineResponse {
  success: boolean;
  deadline?: Deadline;
  error?: string;
}

interface LogDeadlineCalendarExportRequest {
  caseFileId: string;
  deadlineId: string;
  exportedAt: string;
  includedCategories: { recipientName: boolean };
  modifications: { titleModified: boolean; descriptionModified: boolean };
  acknowledged: boolean;
  clientVersion?: string;
}

interface LogDeadlineCalendarExportResponse {
  success: boolean;
  logId?: string;
  error?: string;
}

// ────────────────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────────────────

/** Stable client version for the audit metadata. ≤32 chars per TASK-014. */
const CLIENT_VERSION = 'watchtower-export-1.0.0';

const SUCCESS_BANNER_AUTO_DISMISS_MS = 1800;

const ERROR_BANNER_AUDIT =
  'Could not record this export. Please try again. If this persists, contact support.';
const ERROR_BANNER_GENERATOR = 'Could not build calendar file. Please try again.';
const ERROR_BANNER_DOWNLOAD =
  'Download blocked by your browser. Please allow downloads and try again.';
const ERROR_BANNER_FETCH =
  'Could not load deadline details. Please try again.';
const SUCCESS_BANNER_TEXT = 'Calendar event downloaded.';

const DISCLOSURE_COPY = (
  <>
    <span className="font-semibold">
      Once you download this file, Hesvara cannot control where it goes.
    </span>{' '}
    Calendar apps may sync this event to other devices, third-party services, or
    family-shared calendars depending on your account settings. Review the title
    and description below before exporting — anything you include in this file
    may end up outside Hesvara&apos;s protected environment.
  </>
);

const ACK_LABEL_COPY = (
  <>
    <span className="font-semibold">I understand</span> this calendar event
    leaves Hesvara&apos;s protected environment, and I have reviewed the title
    above for personal information.
  </>
);

const PHI_TIP_COPY = (
  <>
    <span className="font-semibold">💡 Tip:</span> Avoid putting names,
    diagnoses, or other personal health details in the title. Calendar apps may
    sync this title to other devices and services.
  </>
);

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function buildDefaultTitle(
  deadline: Deadline,
  recipientName: string | undefined,
  includeRecipientName: boolean,
): string {
  if (includeRecipientName && recipientName) {
    return `Hesvara Deadline (${recipientName}): ${deadline.title}`;
  }
  return `Hesvara Deadline: ${deadline.title}`;
}

function buildDefaultDescription(
  deadline: Deadline,
  recipientName: string | undefined,
  includeRecipientName: boolean,
): string {
  const typeLabel =
    DEADLINE_TYPES.find((t) => t.value === deadline.type)?.label ?? deadline.type;
  const priorityLabel =
    DEADLINE_PRIORITIES.find((p) => p.value === deadline.priority)?.label ??
    deadline.priority;
  const base =
    `Type: ${typeLabel}\n` +
    `Priority: ${priorityLabel}\n\n` +
    `Managed in Hesvara — open the app for full context.`;
  if (includeRecipientName && recipientName) {
    return `${base}\nFor: ${recipientName}`;
  }
  return base;
}

function triggerBlobDownload(filename: string, ics: string): void {
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    // Some browsers require the anchor to be in the DOM for `.click()` to fire.
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Generic banner copy by error category. Server-side codes are NOT echoed
 * to the user (security posture); they are logged to the console for Phase-4
 * diagnostics. */
function describeAuditError(err: unknown): {
  banner: string;
  diagnostic: string;
} {
  if (err instanceof FunctionsError) {
    return {
      banner: ERROR_BANNER_AUDIT,
      diagnostic: `logDeadlineCalendarExport HttpsError code=${err.code} message=${err.message}`,
    };
  }
  if (err instanceof Error) {
    return { banner: ERROR_BANNER_AUDIT, diagnostic: err.message };
  }
  return { banner: ERROR_BANNER_AUDIT, diagnostic: String(err) };
}

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────

type SubmitPhase = 'idle' | 'auditing' | 'generating' | 'downloading';
type FetchPhase = 'idle' | 'fetching' | 'fetched' | 'fetch-failed';
type BannerKind = 'audit' | 'generator' | 'download' | 'fetch' | 'success';

export function DeadlineCalendarExportModal({
  isOpen,
  onClose,
  deadline,
  deadlineRef,
  recipientName,
  onExported,
}: DeadlineCalendarExportModalProps): JSX.Element | null {
  // Internal preflight fetch state — populated when the modal is opened with
  // a `deadlineRef` instead of a fully-loaded `Deadline`. Owning this here
  // (rather than at the parent) means every export-flow failure mode —
  // preflight, audit, generator, download — surfaces through the same
  // inline error banner. (TASK-016 Codex remediation, finding 2.)
  const [fetchedDeadline, setFetchedDeadline] = useState<Deadline | null>(null);
  const [fetchPhase, setFetchPhase] = useState<FetchPhase>('idle');

  /** Source of truth for the rest of the component. */
  const effectiveDeadline = deadline ?? fetchedDeadline;

  // Default title / description tracked separately so we can re-seed on
  // toggle changes when the user hasn't edited them.
  const [includeRecipientName, setIncludeRecipientName] = useState(false);

  const defaultTitle = useMemo(
    () =>
      effectiveDeadline
        ? buildDefaultTitle(effectiveDeadline, recipientName, includeRecipientName)
        : '',
    [effectiveDeadline, recipientName, includeRecipientName],
  );
  const defaultDescription = useMemo(
    () =>
      effectiveDeadline
        ? buildDefaultDescription(effectiveDeadline, recipientName, includeRecipientName)
        : '',
    [effectiveDeadline, recipientName, includeRecipientName],
  );

  const [title, setTitle] = useState(defaultTitle);
  const [description, setDescription] = useState(defaultDescription);
  const [titleDirty, setTitleDirty] = useState(false);
  const [descriptionDirty, setDescriptionDirty] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitPhase, setSubmitPhase] = useState<SubmitPhase>('idle');
  const [banner, setBanner] = useState<{ kind: BannerKind; text: string } | null>(
    null,
  );

  // Re-seed default title/description when the toggle changes — only fields
  // the user has NOT edited (per-field dirty flag).
  useEffect(() => {
    if (!titleDirty) setTitle(defaultTitle);
    if (!descriptionDirty) setDescription(defaultDescription);
    // intentionally narrow: respond only to default-string changes
  }, [defaultTitle, defaultDescription, titleDirty, descriptionDirty]);

  // Preflight fetch — fires once per open when only a `deadlineRef` was
  // supplied (no preloaded `Deadline`). On failure, surfaces an inline
  // banner with a Retry affordance — same UX as audit/generator/download
  // failures so support has one consistent diagnostic surface.
  //
  // `fetchSeqRef` invalidates stale fetches: every call captures the current
  // sequence id and bails before any setState if the id has been bumped
  // (modal closed/reopened, or a manual retry started). Without this, a
  // close-during-fetch + reopen sequence could write the previous open's
  // result into the new open's state and stall the new fetch behind the
  // `fetchPhase !== 'idle'` guard. (Codex remediation #1+#2.)
  const refCaseFileId = deadlineRef?.caseFileId;
  const refDeadlineId = deadlineRef?.deadlineId;
  const fetchSeqRef = useRef(0);

  const runPreflightFetch = useCallback(async (): Promise<void> => {
    if (!refCaseFileId || !refDeadlineId) return;
    const seq = ++fetchSeqRef.current;
    setFetchPhase('fetching');
    setBanner(null);
    try {
      const callable = httpsCallable<
        { caseFileId: string; deadlineId: string },
        GetDeadlineResponse
      >(functions, 'getDeadline');
      const result = await callable({
        caseFileId: refCaseFileId,
        deadlineId: refDeadlineId,
      });
      if (seq !== fetchSeqRef.current) return; // superseded
      if (!result.data?.success || !result.data.deadline) {
        throw new Error(result.data?.error || 'Failed to load deadline');
      }
      setFetchedDeadline(result.data.deadline);
      setFetchPhase('fetched');
    } catch (err) {
      if (seq !== fetchSeqRef.current) return; // superseded
      console.error('[calendar-export] preflight fetch failure:', err);
      setFetchPhase('fetch-failed');
      setBanner({ kind: 'fetch', text: ERROR_BANNER_FETCH });
    }
  }, [refCaseFileId, refDeadlineId]);

  useEffect(() => {
    if (!isOpen) return;
    if (deadline) return; // preloaded path — no fetch needed
    if (!refCaseFileId || !refDeadlineId) return;
    // Only auto-fire from idle — manual retry uses runPreflightFetch directly.
    if (fetchPhase !== 'idle') return;
    void runPreflightFetch();
  }, [isOpen, deadline, refCaseFileId, refDeadlineId, fetchPhase, runPreflightFetch]);

  // Reset transient state when the modal is closed/re-opened for a new deadline.
  // The sequence-id bump invalidates any in-flight preflight fetch from this
  // open — its eventual `.then`/`.catch` will see `seq !== fetchSeqRef.current`
  // and skip every state setter, so a slow fetch from open #N cannot pollute
  // open #N+1's state.
  useEffect(() => {
    if (!isOpen) {
      fetchSeqRef.current++;
      setIncludeRecipientName(false);
      setTitleDirty(false);
      setDescriptionDirty(false);
      setAcknowledged(false);
      setSubmitPhase('idle');
      setBanner(null);
      setFetchedDeadline(null);
      setFetchPhase('idle');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // PHI invariant: the audit log MUST NOT claim recipient-name was included
  // unless it actually appears in the generated `.ics`. The generator only
  // injects the name when both flags AND a non-empty `recipientName` are
  // present, so we mirror that constraint at the toggle (disabled when name
  // unavailable) and at the audit payload (forced false when name unavailable).
  // (TASK-016 Codex remediation, finding 1.)
  const recipientNameAvailable = Boolean(recipientName);
  const recipientNameWillBeIncluded =
    includeRecipientName && recipientNameAvailable;

  const isSubmitting = submitPhase !== 'idle';
  const inputsDisabled = isSubmitting;
  const downloadDisabled =
    isSubmitting ||
    !effectiveDeadline ||
    !acknowledged ||
    title.trim().length === 0;

  // Diff flags use trimmed-equality vs current default to mirror the
  // generator's contract (TASK-013 Adversarial Review finding 3 / contract
  // lock). PR-2's audit endpoint consumes these.
  const titleModified = title.trim() !== defaultTitle.trim();
  const descriptionModified = description.trim() !== defaultDescription.trim();

  async function handleSubmit(): Promise<void> {
    if (downloadDisabled || !effectiveDeadline) return;
    setBanner(null);

    // ── Step 1: AUDIT FIRST (hard gate) ──────────────────────────────────
    setSubmitPhase('auditing');
    const callable = httpsCallable<
      LogDeadlineCalendarExportRequest,
      LogDeadlineCalendarExportResponse
    >(functions, 'logDeadlineCalendarExport');

    let auditResult: HttpsCallableResult<LogDeadlineCalendarExportResponse>;
    try {
      auditResult = await callable({
        caseFileId: effectiveDeadline.caseFileId,
        deadlineId: effectiveDeadline.deadlineId,
        exportedAt: new Date().toISOString(),
        // Invariant: only `true` when the literal recipient name will land
        // in the `.ics` output. Toggle alone is not sufficient — the name
        // must also be present. (TASK-016 Codex remediation, finding 1.)
        includedCategories: { recipientName: recipientNameWillBeIncluded },
        modifications: { titleModified, descriptionModified },
        acknowledged: true, // server enforces — false would `failed-precondition`
        clientVersion: CLIENT_VERSION,
      });
    } catch (err) {
      const { banner: text, diagnostic } = describeAuditError(err);
      // Generic UX, specific diagnostics (per Phase-4 verification needs)
       
      console.error('[calendar-export] audit failure:', diagnostic, err);
      setSubmitPhase('idle');
      setBanner({ kind: 'audit', text });
      return;
    }

    if (!auditResult.data?.success) {
      const detail = auditResult.data?.error ?? 'no error field on response';
       
      console.error('[calendar-export] audit returned success=false:', detail);
      setSubmitPhase('idle');
      setBanner({ kind: 'audit', text: ERROR_BANNER_AUDIT });
      return;
    }

    // ── Step 2: GENERATE .ics (only on audit success) ────────────────────
    setSubmitPhase('generating');
    let ics: string;
    let filename: string;
    try {
      const options: CalendarExportOptions = {
        includeRecipientName,
        customTitle: titleModified ? title : undefined,
        customDescription: descriptionModified ? description : undefined,
      };
      const result = buildDeadlineIcs(effectiveDeadline, recipientName, options);
      ics = result.ics;
      filename = result.filename;
    } catch (err) {
       
      console.error('[calendar-export] generator failure:', err);
      setSubmitPhase('idle');
      setBanner({ kind: 'generator', text: ERROR_BANNER_GENERATOR });
      // NOTE: an audit_logs entry now exists for an export that did not
      // happen. Acceptable per design doc §2 "Audit POST succeeds but .ics
      // generation throws" — the entry records intent and no PHI escaped.
      return;
    }

    // ── Step 3: TRIGGER DOWNLOAD ─────────────────────────────────────────
    setSubmitPhase('downloading');
    try {
      triggerBlobDownload(filename, ics);
    } catch (err) {
       
      console.error('[calendar-export] download trigger failure:', err);
      setSubmitPhase('idle');
      setBanner({ kind: 'download', text: ERROR_BANNER_DOWNLOAD });
      return;
    }

    // ── Step 4: SUCCESS — green inline banner, then close ────────────────
    // DEVIATION FROM ARCHITECTURE DOC (§2/§4/§6): success state is rendered
    // as an inline green banner inside the still-open modal rather than the
    // toast specified by the doc. No toast pattern exists in the codebase
    // yet; building a one-off in-hook toast risks becoming the de facto
    // pattern by accident. TASK-016 should either introduce a shared toast
    // primitive and migrate this success message, or update the
    // architecture doc to reflect inline-banner as the chosen UX. Verbatim
    // copy "Calendar event downloaded." is preserved.
    setSubmitPhase('idle');
    setBanner({ kind: 'success', text: SUCCESS_BANNER_TEXT });

    onExported({ auditLogged: true });

    setTimeout(() => {
      onClose();
    }, SUCCESS_BANNER_AUTO_DISMISS_MS);
  }

  const downloadButtonLabel =
    submitPhase === 'auditing'
      ? 'Recording export…'
      : submitPhase === 'generating' || submitPhase === 'downloading'
      ? 'Building file…'
      : 'Download .ics';

  // Preview dates for the human-readable preview pane.
  const previewDate = (() => {
    if (!effectiveDeadline) return '';
    try {
      const d = new Date(effectiveDeadline.dueDate + 'T00:00:00');
      return d.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return effectiveDeadline.dueDate;
    }
  })();

  const dialogId = `deadline-calendar-export-${effectiveDeadline?.deadlineId ?? 'pending'}`;
  const showPreflightPane =
    !effectiveDeadline &&
    (fetchPhase === 'fetching' || fetchPhase === 'fetch-failed' || fetchPhase === 'idle');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/40"
      role="presentation"
      data-testid="calendar-export-modal-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${dialogId}-title`}
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
      >
        {/* ── Sticky header ──────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-soft-neutral">
          <h2 id={`${dialogId}-title`} className="text-lg font-semibold text-ink">
            Export to Calendar
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close"
            className="p-1 rounded-full hover:bg-gray-100 text-muted disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Scrollable body ────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Preflight pane — when the modal was opened with only a
              `deadlineRef`, we render the loading or fetch-error state
              here in the same modal frame. (TASK-016 Codex remediation,
              finding 2.) */}
          {showPreflightPane ? (
            <div
              className="py-8 flex flex-col items-center gap-4"
              data-testid="preflight-pane"
            >
              {fetchPhase !== 'fetch-failed' && (
                <div
                  role="status"
                  aria-live="polite"
                  className="flex items-center gap-2 text-muted text-sm"
                >
                  <Loader2 size={18} className="animate-spin" aria-hidden />
                  <span>Loading deadline details…</span>
                </div>
              )}
              {fetchPhase === 'fetch-failed' && banner && (
                <>
                  <div
                    role="alert"
                    data-testid="error-banner"
                    data-banner-kind={banner.kind}
                    className="w-full rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 flex items-start gap-2"
                  >
                    <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                    <span>{banner.text}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => void runPreflightFetch()}
                    className="px-4 py-2 text-sm font-medium text-white bg-evergreen rounded-md hover:bg-deep-forest"
                    data-testid="retry-fetch-button"
                  >
                    Retry
                  </button>
                </>
              )}
            </div>
          ) : (
          <>
          {/* Single PHI toggle */}
          <div>
            <label
              className={`flex items-center gap-2 ${
                inputsDisabled || !recipientNameAvailable
                  ? 'cursor-not-allowed'
                  : 'cursor-pointer'
              }`}
            >
              <input
                type="checkbox"
                checked={includeRecipientName}
                onChange={(e) => setIncludeRecipientName(e.target.checked)}
                disabled={inputsDisabled || !recipientNameAvailable}
                className="w-4 h-4 accent-evergreen disabled:opacity-50"
                data-testid="toggle-include-recipient-name"
              />
              <span
                className={`text-sm ${
                  recipientNameAvailable ? 'text-ink' : 'text-muted'
                }`}
              >
                Include care recipient name
              </span>
            </label>
            <p className="ml-6 text-xs text-muted mt-1">
              {recipientNameAvailable ? (
                <>
                  Default: off. When on, the recipient&apos;s name appears in
                  the event title and description.
                </>
              ) : (
                <span data-testid="recipient-name-unavailable">
                  Care recipient name is not available, so it cannot be
                  included in this export.
                </span>
              )}
            </p>
          </div>

          {/* PHI tip — persistent, muted-yellow, NOT dismissible */}
          <div
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            role="note"
            data-testid="phi-tip"
          >
            {PHI_TIP_COPY}
          </div>

          {/* Title input */}
          <div>
            <label
              htmlFor={`${dialogId}-title-input`}
              className="block text-sm font-medium text-ink mb-1"
            >
              Event title
            </label>
            <input
              id={`${dialogId}-title-input`}
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setTitleDirty(true);
              }}
              disabled={inputsDisabled}
              className="w-full rounded-md border border-soft-neutral px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-evergreen disabled:bg-gray-50 disabled:text-muted"
              data-testid="title-input"
            />
          </div>

          {/* Literal-title preview block — high-contrast, byte-for-byte */}
          <div
            className="rounded-md border-2 border-ink bg-soft-neutral/40 px-4 py-3"
            data-testid="literal-title-preview"
          >
            <p className="text-xs uppercase tracking-wide font-semibold text-muted">
              TITLE (visible in calendar invites and notifications):
            </p>
            <p
              className="mt-2 font-mono text-sm text-ink whitespace-pre-wrap break-all"
              data-testid="literal-title-text"
            >
              {title}
            </p>
            <p className="mt-2 text-xs flex items-center gap-1 text-amber-700">
              <AlertTriangle size={12} />
              This exact text will appear in your calendar app.
            </p>
          </div>

          {/* Description textarea */}
          <div>
            <label
              htmlFor={`${dialogId}-description-input`}
              className="block text-sm font-medium text-ink mb-1"
            >
              Event description
            </label>
            <textarea
              id={`${dialogId}-description-input`}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setDescriptionDirty(true);
              }}
              disabled={inputsDisabled}
              rows={5}
              className="w-full rounded-md border border-soft-neutral px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-evergreen disabled:bg-gray-50 disabled:text-muted font-mono"
              data-testid="description-input"
            />
          </div>

          {/* Rendered preview pane */}
          <div className="rounded-md border border-soft-neutral bg-white px-4 py-3 text-sm">
            <p className="text-xs uppercase tracking-wide font-semibold text-muted mb-2">
              Preview
            </p>
            <dl className="space-y-1">
              <div className="flex gap-2">
                <dt className="text-muted w-24 flex-shrink-0">Title:</dt>
                <dd className="text-ink">{title}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-muted w-24 flex-shrink-0">Date:</dt>
                <dd className="text-ink">{previewDate} (all day)</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-muted w-24 flex-shrink-0">Reminders:</dt>
                <dd className="text-ink">7 days before, 1 day before</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-muted w-24 flex-shrink-0">Description:</dt>
                <dd className="text-ink whitespace-pre-wrap font-mono text-xs">
                  {description}
                </dd>
              </div>
            </dl>
            <p className="mt-3 text-xs">
              This event will sync to your calendar app.
            </p>
            {recipientNameWillBeIncluded ? (
              <p
                className="mt-1 text-xs flex items-center gap-1 text-amber-700"
                data-testid="preview-phi-included"
              >
                <AlertTriangle size={12} />
                Includes care recipient name: {recipientName}
              </p>
            ) : (
              <p
                className="mt-1 text-xs flex items-center gap-1 text-evergreen"
                data-testid="preview-phi-excluded"
              >
                <CheckCircle2 size={12} />
                No care recipient name included
              </p>
            )}
          </div>

          {/* Disclosure copy */}
          <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 leading-relaxed">
            {DISCLOSURE_COPY}
          </div>

          {/* Acknowledgment checkbox */}
          <label
            className="flex items-start gap-2 cursor-pointer"
            data-testid="ack-label"
          >
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              disabled={inputsDisabled}
              className="mt-0.5 w-4 h-4 accent-evergreen flex-shrink-0"
              data-testid="ack-checkbox"
            />
            <span className="text-sm text-ink leading-relaxed">
              {ACK_LABEL_COPY}
            </span>
          </label>

          {/* Error / success banners. `kind: 'fetch'` is rendered in the
              preflight pane above and never reaches this branch, but the
              explicit exclusion documents the invariant. */}
          {banner && banner.kind !== 'success' && banner.kind !== 'fetch' && (
            <div
              role="alert"
              data-testid="error-banner"
              data-banner-kind={banner.kind}
              className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 flex items-start gap-2"
            >
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{banner.text}</span>
            </div>
          )}
          {banner && banner.kind === 'success' && (
            <div
              role="status"
              data-testid="success-banner"
              className="rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800 flex items-start gap-2"
            >
              <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" />
              <span>{banner.text}</span>
            </div>
          )}
          </>
          )}
        </div>

        {/* ── Sticky footer ──────────────────────────────────────────── */}
        <div className="px-5 py-3 border-t border-soft-neutral bg-gray-50 flex justify-end gap-3 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-ink bg-white border border-soft-neutral rounded-md hover:bg-gray-50"
            data-testid="cancel-button"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={downloadDisabled}
            className="px-4 py-2 text-sm font-medium text-white bg-evergreen rounded-md hover:bg-deep-forest disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[10rem] justify-center"
            data-testid="download-button"
          >
            {isSubmitting && (
              <Loader2 size={14} className="animate-spin" aria-hidden />
            )}
            {downloadButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeadlineCalendarExportModal;
